/**
 * @fileoverview Hook Data Layer para la gestión y carga de tags de cliente en el módulo Inbox.
 * Implementa el patrón de Data Layer con separación estricta de responsabilidades, optimización de rendimiento y debug tracking avanzado.
 * @module useCustomerDetailTagsData
 * @category Hook - Data Layer
 * @since 3.1.0
 * @version 3.1.0
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getTagsAlways, getTagsIfEmpty } from '../../../../actions/inbox/tagActions';

/**
 * @typedef {Object} TagOption
 * @description Objeto de opción de tag para selección en formulario.
 * @property {string|number} id - Identificador único del tag
 * @property {string} value - Valor del tag (igual al id)
 * @property {string} label - Etiqueta visible del tag
 * @property {boolean} isLocal - Indica si el tag fue creado localmente
 */

/**
 * Hook Data Layer especializado para la carga y gestión de tags de cliente en el Inbox.
 *
 * Implementa el patrón de **Data Layer** con separación estricta de responsabilidades:
 * - Solo carga de datos y gestión de estados de API
 * - Sin lógica de UI ni operaciones de negocio
 *
 * **Responsabilidades:**
 * 1. Cargar tags desde Redux y API
 * 2. Permitir refresco manual de tags
 * 3. Gestionar tags creados localmente
 * 4. Validar existencia de tags por nombre
 * 5. Exponer opciones de tags para formularios
 *
 * **NO maneja:**
 * - ❌ Estado visual de UI
 * - ❌ Operaciones de negocio (guardar, eliminar)
 * - ❌ Validación de formularios
 *
 * **Flujo de operación:**
 * 1. Al montar, carga tags si están vacíos (getTagsIfEmpty)
 * 2. Permite refresco manual con refreshTags (getTagsAlways)
 * 3. Memoiza opciones de tags (remotos y locales)
 * 4. Expone función para validar existencia de tag por nombre
 *
 * @param {Array<TagOption>} [initialTags=[]] - Array inicial de tags seleccionados
 *
 * @returns {Object} Objeto con datos y funciones para gestión de tags
 * @property {Array<TagOption>} tagOptions - Opciones de tags para selección
 * @property {Array<TagOption>} localCreatedTags - Tags creados localmente
 * @property {Array<string|number>} initialSelectedTags - IDs de tags seleccionados inicialmente
 * @property {Array<TagOption>} tagOptionsRedux - Tags cargados desde Redux
 * @property {function} refreshTags - Función para refrescar tags desde API
 * @property {function} setLocalCreatedTags - Setter para tags locales
 * @property {function} checkTagExists - Verifica si existe un tag por nombre
 *
 * @see {@link ../../modules/inbox/components/CustomerDetailFormMC.js} Componente relacionado
 * @see {@link ../../data/useCustomerDetailData.js} Hook de datos de customer
 * @see {@link ../../actions/useCustomerDetailActions.js} Hook de acciones de customer
 *
 * @example
 * const {
 *   tagOptions,
 *   localCreatedTags,
 *   initialSelectedTags,
 *   refreshTags,
 *   setLocalCreatedTags,
 *   checkTagExists
 * } = useCustomerDetailTagsData(customer.tags);
 */

const useCustomerDetailTagsData = (initialTags = []) => {
  const dispatch = useDispatch();
  const tagOptionsRedux = useSelector((state) => state.tagsReducer?.tags || []);
  const [localCreatedTags, setLocalCreatedTags] = useState([]);


  /**
   * Refresca la lista de tags desde la API.
   * @function refreshTags
   * @memberof useCustomerDetailTagsData
   * @description Llama a la acción Redux para cargar todos los tags.
   * @returns {void}
   * @example
   * refreshTags();
   */
  const refreshTags = useCallback(() => {
    dispatch(getTagsAlways());
  }, [dispatch]);


  // Carga inicial de tags si Redux está vacío
  useEffect(() => {
    dispatch(getTagsIfEmpty());
  }, [dispatch]);


  /**
   * Memoiza y combina las opciones de tags remotos y locales.
   * @type {Array<TagOption>}
   */
  const tagOptions = useMemo(() => {
    const remoteTags = Array.isArray(tagOptionsRedux)
      ? tagOptionsRedux.map((tag) => ({
          id: tag.id,
          value: tag.id,
          label: tag.label || tag.name,
          isLocal: false
        }))
      : [];

    const localTags = localCreatedTags.map((tag) => ({
      id: tag.id,
      value: tag.id,
      label: tag.label,
      isLocal: true
    }));

    return [...remoteTags, ...localTags];
  }, [tagOptionsRedux, localCreatedTags]);


  /**
   * Memoiza los IDs de los tags seleccionados inicialmente.
   * @type {Array<string|number>}
   */
  const initialSelectedTags = useMemo(() => {
    return initialTags.map((tag) => tag.id);
  }, [initialTags]);


  /**
   * Verifica si existe un tag por nombre (remoto o local).
   * @function checkTagExists
   * @memberof useCustomerDetailTagsData
   * @param {string} tagName - Nombre del tag a verificar
   * @returns {boolean} True si existe, false si no
   * @example
   * const exists = checkTagExists('VIP');
   */
  const checkTagExists = useCallback((tagName) => {
    const existsInRemote = tagOptionsRedux.some(tag =>
      tag.label?.toLowerCase() === tagName.toLowerCase() ||
      tag.name?.toLowerCase() === tagName.toLowerCase()
    );

    const existsInLocal = localCreatedTags.some(tag =>
      tag.label?.toLowerCase() === tagName.toLowerCase()
    );

    return existsInRemote || existsInLocal;
  }, [tagOptionsRedux, localCreatedTags]);

  /**
   * Retorno memoizado del hook con todas las funciones y datos expuestos.
   * @returns {Object} Objeto con datos y funciones para gestión de tags
   */
  return useMemo(() => ({
    tagOptions,
    localCreatedTags,
    initialSelectedTags,
    tagOptionsRedux,
    refreshTags,
    setLocalCreatedTags,
    checkTagExists
  }), [
    tagOptions,
    localCreatedTags,
    initialSelectedTags,
    tagOptionsRedux,
    refreshTags,
    setLocalCreatedTags,
    checkTagExists
  ]);
};

export default useCustomerDetailTagsData;